import { MODULE_NAME } from './const';
import { selectedEmailTemplateId, defaultEmailTemplate } from './getters';
import {
	SET_EMAIL_TEMPLATES,
	SET_PUBLIC_EMAIL_TEMPLATES,
	SET_SELECTED_EMAIL_TEMPLATE_ID,
	ADD_EMAIL_TEMPLATE,
	DELETE_EMAIL_TEMPLATE,
	UPDATE_EMAIL_TEMPLATE,
} from './mutations';
import StoreModuleService from '../StoreModuleService';
import { updateAccount } from '../account/actions';
import api from '@/api';
import { arrayToObject } from '@/utils/helpers';

const buildActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

let _fetchEmailTemplatesPromise = null;
let _fetchPublicEmailTemplatesPromise = null;

// actions declarations
export const addTemplate = buildActionObject('addTemplate');
export const deleteTemplate = buildActionObject('deleteTemplate');
export const duplicateTemplate = buildActionObject('duplicateTemplate');
export const setDefaultEmailTemplate = buildActionObject('setDefaultEmailTemplate');
export const duplicateAndSetAsDefault = buildActionObject('duplicateAndSetAsDefault');
export const updateEmailTemplate = buildActionObject('updateEmailTemplate');
export const fetchEmailTemplates = buildActionObject('fetchEmailTemplates');
export const fetchPublicEmailTemplates = buildActionObject('fetchPublicEmailTemplates');
export const fetchAllEmailTemplates = buildActionObject('fetchAllEmailTemplates');
export const setSelectedEmailTemplate = buildActionObject('setSelectedEmailTemplate');
export const setEmailTemplates = buildActionObject('setEmailTemplates');
export const setPublicEmailTemplates = buildActionObject('setPublicEmailTemplates');
export const fetchWorkflowsRelatedToTemplateId = buildActionObject('fetchWorkflowsRelatedToTemplateId');

// private actions
export const _setSelectedEmailTemplateId = buildActionObject('_setSelectedEmailTemplateId');

// TODO create an action for fetch all email templates
const actions = {
	...StoreModuleService.createDefaultActions(),
	[fetchEmailTemplates.action]({ dispatch }) {
		// If fetch email templates is already in progress, return the promise
		if (_fetchEmailTemplatesPromise) {
			return _fetchEmailTemplatesPromise;
		}
		_fetchEmailTemplatesPromise = async () => {
			try {
				const emailTemplates = await api.listEmailTemplates();
				const emailTemplatesObj = arrayToObject(emailTemplates);
				await dispatch(setEmailTemplates.action, { emailTemplates: emailTemplatesObj });
			} finally {
				_fetchEmailTemplatesPromise = null;
			}
		};
		return _fetchEmailTemplatesPromise();
	},
	async [fetchPublicEmailTemplates.action]({ dispatch }) {
		// If fetch public email templates is already in progress, return the promise
		if (_fetchPublicEmailTemplatesPromise) {
			return _fetchPublicEmailTemplatesPromise;
		}
		_fetchPublicEmailTemplatesPromise = async () => {
			try {
				const publicEmailTemplates = await api.listPublicEmailTemplates();
				const publicEmailTemplatesObj = arrayToObject(publicEmailTemplates);
				await dispatch(setPublicEmailTemplates.action, { publicEmailTemplates: publicEmailTemplatesObj });
			} finally {
				_fetchPublicEmailTemplatesPromise = null;
			}
		};
		return _fetchPublicEmailTemplatesPromise();
	},
	async [fetchAllEmailTemplates.action]({ dispatch }) {
		await dispatch(fetchEmailTemplates.action);
		await dispatch(fetchPublicEmailTemplates.action);
	},
	async [duplicateAndSetAsDefault.action]({ dispatch }, { templateId, isPublic }) {
		const res = await dispatch(duplicateTemplate.action, { templateId, isPublic });
		await dispatch(setDefaultEmailTemplate.action, { templateId: res.id });
		await dispatch(fetchEmailTemplates.action); // Makes sure that the email template is updated with the 'is_default_of_acc' field
	},
	async [updateEmailTemplate.action]({ commit }, { id, payload }) {
		const resp = await api.updateEmailTemplate(id, payload);
		commit(UPDATE_EMAIL_TEMPLATE, resp);
	},
	async [setDefaultEmailTemplate.action]({ dispatch }, { templateId }) {
		await dispatch(updateAccount.fullPath, { default_email_template: templateId }, { root: true });
	},
	async [duplicateTemplate.action]({ commit }, { templateId, isPublic }) {
		let resp;
		if (isPublic) {
			resp = await api.duplicatePublicEmailTemplate(templateId);
		} else {
			resp = await api.duplicateTemplate(templateId);
		}
		commit(ADD_EMAIL_TEMPLATE, resp);
		return resp;
	},
	async [deleteTemplate.action]({ commit, dispatch, getters }, templateId) {
		const deletedTemplateIsSelected = getters[selectedEmailTemplateId.getter] === templateId;
		if (deletedTemplateIsSelected) {
			// if the template being deleted is the selected template, set default template as selected
			const firstEmailTemplateId = getters[defaultEmailTemplate.getter].id;
			await dispatch(_setSelectedEmailTemplateId.action, firstEmailTemplateId);
		}
		await api.deleteEmailTemplate(templateId);
		commit(DELETE_EMAIL_TEMPLATE, templateId);
	},
	async [addTemplate.action]({ commit }, payload) {
		const { title, design } = payload;
		const resp = await api.addEmailTemplate({ title, design });
		commit(ADD_EMAIL_TEMPLATE, resp);
		return resp;
	},
	async [fetchWorkflowsRelatedToTemplateId.action](_, { templateId }) {
		return api.templateUsedInWorkflowsCount(templateId);
	},
	[setEmailTemplates.action]({ commit }, { emailTemplates }) {
		commit(SET_EMAIL_TEMPLATES, emailTemplates);
	},
	[setPublicEmailTemplates.action]({ commit }, { publicEmailTemplates }) {
		commit(SET_PUBLIC_EMAIL_TEMPLATES, publicEmailTemplates);
	},
	async [setSelectedEmailTemplate.action]({ dispatch }, { templateId }) {
		await dispatch(_setSelectedEmailTemplateId.action, templateId);
	},
	[_setSelectedEmailTemplateId.action]({ commit }, templateId) {
		commit(SET_SELECTED_EMAIL_TEMPLATE_ID, templateId);
	},
};
export default actions;
